import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, X, NotePencil, Tag } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { formatMoney } from "./utils";
import type { CashierLabels, CashierOrderLine } from "./types";

export interface CashierCartItemProps {
  line: CashierOrderLine;
  onQtyChange: (lineId: string, qty: number) => void;
  onRemove: (lineId: string) => void;
  onOpenLineActions?: (lineId: string) => void;
  labels: CashierLabels;
  locale?: "tr" | "en";
}

export function CashierCartItem({
  line, onQtyChange, onRemove, onOpenLineActions, labels, locale = "tr",
}: CashierCartItemProps) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -8, height: 0, marginTop: 0, paddingTop: 0, paddingBottom: 0 }}
      transition={{ duration: 0.18 }}
      className="rounded-[12px] border border-biz-border bg-biz-surface-elevated p-3"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-biz-foreground leading-snug">{line.label}</p>
            <button
              type="button"
              aria-label={labels.cart.remove}
              onClick={() => onRemove(line.lineId)}
              className={cn(
                "h-11 w-11 -mr-2 -mt-1 rounded-biz-btn flex items-center justify-center",
                "text-biz-foreground-muted hover:text-biz-danger hover:bg-biz-danger-subtle",
                "transition-colors biz-focus-ring",
              )}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-1 flex items-center gap-2">
            <span className="font-mono-num text-xs text-biz-foreground-muted tabular-nums">
              {formatMoney(line.unitPriceMinor, labels.currencyPrefix, locale)}
            </span>
            {line.discount && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-biz-success-subtle text-biz-success">
                <Tag weight="fill" className="h-3 w-3" />
                {line.discount.kind === "percent"
                  ? `%${line.discount.valueMinor}`
                  : `-${formatMoney(line.discount.valueMinor, labels.currencyPrefix, locale)}`}
              </span>
            )}
            {line.note && (
              <span className="inline-flex items-center gap-1 text-[10px] text-biz-foreground-subtle">
                <NotePencil className="h-3 w-3" />
                {line.note}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <QtyButton
            label={labels.cart.qtyDecrement}
            onClick={() => onQtyChange(line.lineId, Math.max(0, line.qty - 1))}
          >
            <Minus className="h-4 w-4" />
          </QtyButton>
          <span className="font-mono-num text-sm font-bold text-biz-foreground min-w-[2ch] text-center tabular-nums">
            {line.qty}
          </span>
          <QtyButton
            label={labels.cart.qtyIncrement}
            onClick={() => onQtyChange(line.lineId, line.qty + 1)}
          >
            <Plus className="h-4 w-4" />
          </QtyButton>
          {onOpenLineActions && (
            <button
              type="button"
              aria-label={labels.cart.lineDiscount}
              onClick={() => onOpenLineActions(line.lineId)}
              className="ml-1 h-9 px-2 rounded-biz-btn text-xs font-semibold text-biz-primary hover:bg-biz-primary-subtle biz-focus-ring transition-colors"
            >
              <Tag className="h-4 w-4" />
            </button>
          )}
        </div>

        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={line.lineTotalMinor - (line.discount?.valueMinor ?? 0)}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.18 }}
            className="font-mono-num text-base font-bold text-biz-foreground tabular-nums"
          >
            {formatMoney(line.lineTotalMinor - (line.discount?.valueMinor ?? 0), labels.currencyPrefix, locale)}
          </motion.span>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function QtyButton({ children, label, onClick }: { children: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        "h-9 w-9 rounded-biz-btn flex items-center justify-center",
        "border border-biz-border bg-biz-surface text-biz-foreground hover:bg-biz-muted",
        "transition-colors biz-focus-ring",
      )}
    >
      {children}
    </button>
  );
}
